import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnInit, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NgScrollbar } from 'ngx-scrollbar';
import { Observable } from 'rxjs';
import { MoneyPipe } from '../../../pipes/framework/money-short.pipe';
import { CurrentUserService } from '../../../services/current-user.service';
import { InteractionBarStateService } from '../../../services/interaction-bar-state.service';
import { NotificationsService } from '../../../services/notifications.service';
import { ProjectSpendService } from '../../../services/project-spend.service';
import { selectAllLineItems, selectAllSpendSubItems } from '../../../store/spend/spend.selectors';
import { viewProjectSelectors } from '../../../store/view-project/view-project.selectors';
import { BorderedDropdownIconComponent } from '../../bordered-dropdown-icon/bordered-dropdown-icon.component';
import { SimpleButtonComponent } from '../../buttons/simple-medium-button/simple-button.component';
import { INTERACTION_BAR_STATES } from '../../constants/interaction-bar.constants';
import {
  MANAGE_PROJECT_TABS,
  VIEW_PROJECT_REFRESH_CAUSE,
} from '../../constants/view-project.constants';
import { GeneralSidebarHeaderComponent } from '../../general-sidebar-header/general-sidebar-header.component';
import { DropdownComponent } from '../../inputs/dropdown/dropdown.component';
import { FloatingInputComponent } from '../../inputs/floating-input/floating-input.component';
import { PageLoadingComponent } from '../../page-loading/page-loading.component';
import { AddRemoveButtonsComponent } from '../../buttons/add-remove-buttons/add-remove-buttons.component';
import {
  IForecastModification,
  IForecastModificationBudgetLine,
} from './forecast-modifications-sidebar.type';
import { viewProjectActions } from '@app/store/view-project/view-project.actions';
import { DeepCopyService } from '@app/services/deep-copy.service';

type ForecastModificationsPostRequest = {
  forecast_modifications: {
    item_id: number;
    modifications: { note: string; value: number }[];
  }[];
};

@Component({
  selector: 'app-forecast-modifications-sidebar',
  imports: [
    GeneralSidebarHeaderComponent,
    NgScrollbar,
    SimpleButtonComponent,
    FloatingInputComponent,
    DropdownComponent,
    NgClass,
    FormsModule,
    AsyncPipe,
    PageLoadingComponent,
    MoneyPipe,
    BorderedDropdownIconComponent,
    AddRemoveButtonsComponent,
  ],
  templateUrl: './forecast-modifications-sidebar.component.html',
  styleUrl: './forecast-modifications-sidebar.component.scss',
})
export class ForecastModificationsSidebarComponent implements OnInit {
  @ViewChild('forecastModsForm') forecastModsForm: NgForm;

  private readonly store = inject(Store);
  private readonly notif = inject(NotificationsService);
  private readonly interactionBar = inject(InteractionBarStateService);
  private readonly user = inject(CurrentUserService);
  private readonly projectSpendService = inject(ProjectSpendService);

  readonly isSending = signal(false);
  readonly isLoading$ = this.store.select(viewProjectSelectors.isLoadingForecastModifications);

  // Provide initialValue so we can use the signal immediately
  readonly allForecastMods = toSignal<IForecastModificationBudgetLine[]>(
    this.store.select(viewProjectSelectors.selectAllForecastModifications),
  );

  private initialForecastMods: IForecastModificationBudgetLine[] = [];
  readonly forecastModifications = signal<IForecastModificationBudgetLine[]>([]);

  private readonly lineItems$: Observable<{ id: number; name: string }[]> = this.user.isPremiumSP
    ? this.store.select(selectAllSpendSubItems)
    : this.store.select(selectAllLineItems);

  readonly lineItemOptions = toSignal(this.lineItems$);

  constructor() {
    effect(() => {
      const forecastMods = this.allForecastMods();
      if (forecastMods) {
        this.initialForecastMods = forecastMods.filter(
          (mod) => (mod.forecast_modifications?.length ?? 0) > 0 || mod.scos?.length > 0,
        );
        const deepCopy = DeepCopyService.deepCopy(this.initialForecastMods);
        this.forecastModifications.set(deepCopy);
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(viewProjectActions.loadForecastModifications());
  }

  save() {
    this.isSending.set(true);
    this.forecastModsForm.form.markAllAsTouched();

    if (!this.forecastModsForm.form.valid) {
      this.isSending.set(false);
      this.notif.showError('The form is invalid.');
      return;
    }

    this.modifyForecast(this.forecastModifications());
  }

  private modifyForecast(forecastMods: IForecastModificationBudgetLine[]) {
    // deleted items have to be submitted with empty modifications due to backend requirements
    const deletedMods = this.initialForecastMods.filter(
      (mod) => !forecastMods.find((newMod) => newMod.id === mod.id),
    );

    const body: ForecastModificationsPostRequest = {
      forecast_modifications: [
        ...forecastMods.map((item) => ({
          item_id: item.id,
          modifications: item.forecast_modifications,
        })),
        ...deletedMods.map((item) => ({
          item_id: item.id,
          modifications: [],
        })),
      ],
    };

    this.projectSpendService.modifyForecast(body).then(
      () => {
        this.notif.showSuccess('Input saved!');
        setTimeout(() => {
          this.store.dispatch(
            viewProjectActions.refreshNeeded({
              cause: VIEW_PROJECT_REFRESH_CAUSE.INTERACTION_BAR_CLOSED,
            }),
          );
          this.isSending.set(false);
          this.interactionBar.close();
        }, 200);
      },
      (err) => {
        this.isSending.set(false);
        console.warn('Forecast error', err);
        this.notif.showError(err);
      },
    );
  }

  deleteItem(index: number, itemIndex: number) {
    const mods = this.forecastModifications();
    mods[index].forecast_modifications.splice(itemIndex, 1);
    this.forecastModifications.set(mods);
  }

  /**
   * Adds a new forecast modification description.
   * @param index The index of the forecast modification to which the description will be added.
   * @param descriptionIndex The index of the description after which the new description will be added. (Default is -1. This means the description will be added at the beginning.)
   */
  insertItem(index: number, descriptionIndex = -1) {
    const mods = this.forecastModifications();
    const modification: IForecastModification = {
      note: '',
      value: null,
      deleted_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mods[index].forecast_modifications.splice(descriptionIndex + 1, 0, modification);
    this.forecastModifications.set(mods);
  }

  budgetLineChanged(budgetLineId: number, index: number) {
    // without this function, the dropdown would trigger computed fields for state
    this.forecastModifications.set(
      this.forecastModifications().map((mod, i) =>
        i === index ? { ...mod, id: budgetLineId } : mod,
      ),
    );
  }

  addBudgetLine() {
    this.forecastModifications().push({
      id: null,
      name: '',
      forecast_modifications: [{ note: '', value: null }] as IForecastModification[],
      current_budget: 0,
      original_budget: 0,
      scos: [],
    });
  }

  removeBudgetLine(index: number) {
    this.notif
      .showPopup(`Are you sure you want to clear cost group ${index + 1}?`)
      .then((shouldDelete) => {
        if (!shouldDelete) return;
        const mods = DeepCopyService.deepCopy(this.forecastModifications()); // deep copy needed here to trigger computed field updates
        mods.splice(index, 1);
        this.forecastModifications.set(mods);
      });
  }

  clearAllCosts() {
    this.notif.showPopup('Are you sure you want to clear all cost entries?').then((shouldClear) => {
      if (shouldClear) {
        this.forecastModifications.set(
          this.forecastModifications()
            .map((mods) => ({ ...mods, modifications: [] }))
            .filter((mods) => mods.scos && mods.scos.length > 0),
        );
      }
    });
  }

  readonly total = computed(
    () =>
      this.forecastModifications()?.reduce((acc, curr) => {
        const mods = curr?.forecast_modifications ?? [];
        const scos = curr?.scos ?? [];
        const totalValue = [...mods, ...scos].reduce(
          (sum, modification) => sum + (Number(modification.value) || 0),
          0,
        );
        return acc + totalValue;
      }, 0) ?? 0,
  );

  readonly disabledOptionIds = computed(() => {
    const forecastMods = this.forecastModifications();
    return new Set(forecastMods.map((mod) => mod.id).filter((id) => id !== null));
  });

  switchViewToManageProject() {
    this.interactionBar.openInteractionBar(INTERACTION_BAR_STATES.MANAGE_PROJECT, {
      tab: MANAGE_PROJECT_TABS.BUDGET_CASHFLOW,
    });
  }
}
